<template>
  <div class="grid grid-pad box-size" style="overflow: initial">
    <div class="col-1-1 text-dark">
      <div class="substitution">
        <div class="header width-100">
          <div class="flex space-between align-center">
            <div class="substitution__header__left flex align-center">
              <h1>{{ $t('vue2.substitution.substitution') }}</h1>
              <div
                class="substitution__settings"
                :class="{'substitution__settings--open': isSubstitutionSettingsOpen}"
                v-on-clickaway="closeSubstitutionSettings">
                <button
                  type="button"
                  class="substitution__settings__toggle"
                  :aria-expanded="isSubstitutionSettingsOpen ? 'true' : 'false'"
                  :aria-label="$t('vue2.substitution.settings')"
                  @click="isSubstitutionSettingsOpen = !isSubstitutionSettingsOpen">
                  <span class="icon-more2"></span>
                </button>
                <div
                  v-if="isSubstitutionSettingsOpen"
                  class="substitution__settings__dropdown">
                  <div class="substitution__settings__label">
                    {{ $t('vue2.substitution.settings') }}
                  </div>
                  <button
                    type="button"
                    class="substitution__settings__item"
                    :class="{'substitution__settings__item--active': includeReservationsInSubstitution}"
                    @click="toggleIncludeReservations">
                    <span
                      class="substitution__settings__checkbox"
                      :class="{'substitution__settings__checkbox--checked': includeReservationsInSubstitution}"
                      aria-hidden="true">
                    </span>
                    <span class="substitution__settings__item-text">
                      {{ $t('vue2.substitution.include_reservations') }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="substitution__settings__item"
                    :class="{'substitution__settings__item--active': showOverlappingClassBadgeInSubstitution}"
                    @click="toggleShowOverlappingClassBadge">
                    <span
                      class="substitution__settings__checkbox"
                      :class="{'substitution__settings__checkbox--checked': showOverlappingClassBadgeInSubstitution}"
                      aria-hidden="true">
                    </span>
                    <span class="substitution__settings__item-text">
                      {{ $t('vue2.substitution.show_overlapping_class_badge') }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div class="substitution__header__right">
              <SubsAbsentWithoutEnd></SubsAbsentWithoutEnd>
              <SubsNote :date="getDate" v-if="userCanSeeInternalNote"></SubsNote>
              <button class="marg-10-right no-print aku-button"
                      @click="setIsModalClassOnTrip(true)"
                      v-if="isClassOnTripSchool">
                Opret klassefravær
              </button>
              <button @click="print"
                      v-if="!localSchedulerView"
                      class="marg-10-right no-print"
                      style="height: 38px;width: 38px">
                <span class="icon-printer3"></span>
              </button>
              <button @click="setToday"
                      :class="{inactive: isToday}"
                      class="date-buttons marg-5-right">
                {{ $t('vue2.substitution.today') }}
              </button>
              <button @click="prevDay" class="date-buttons marg-5-right">
                <span class="icon-arrow-left4"></span>
              </button>
              <DatePicker
                :date="getDate"
                @changed="setDate">
              </DatePicker>
              <button @click="nextDay" class="marg-5-left date-buttons">
                <span class="icon-arrow-right4"></span>
              </button>
              <SubstitutionCreate
                :schoolId="schoolId"
                :users="allUsersSortedOnName"
                :usersWithOngoingAbsenceInHash="usersWithOngoingAbsenceInHash"
                :allUsersInCalIdHash="allUsersInCalIdHash">
              </SubstitutionCreate>
            </div>
          </div>
        </div>
        <div class="sub-header__subHeader">
          <Tabs
            :tabs="absentTabs"
            :selected="currentAbsentTabId"
            @select="onAbsentTabSelect"
          />
          <app-flipper-button
            :preselectedTab="activeTab"
            @update="setSchedulerView">
          </app-flipper-button>
        </div>
        <spinner v-if="(isLoadingSchoolCalenders || dataIsLoading) && !localSchedulerView" color="blue"
                 style="margin-top: 150px"></spinner>
        <template v-if="!localSchedulerView && !dataIsLoading && !isLoadingSchoolCalenders">
          <SubsUser v-for="(user, idx) in absentUsers"
                    :user="user"
                    :activeDSelect="activeDSelect"
                    :key="user.id"
                    :date="getDate"
                    :allEvents="allEvents"
                    :calsAndEventIds="calsAndEventIds"
                    :allEventsInAHash="allEventsInAHash"
                    :allUsersSortedOnName="allUsersSortedOnName"
                    :allUsersAndEvents="allUsersAndEvents"
                    :allCalendarsInAHash="allCalendarsInAHash"
                    :longAbsentListInHash="longAbsentListInHash"
                    :absentUsersHandledHash="absentUsersHandledHash"
                    :isSubStatusPendingActive="isSubStatusPendingActive"
                    @splitLesson="setSplitLesson"
                    @setActiveDSelect="setActiveDSelect"
                    @selectedUser="selectedUser = user"
                    @removeSubs="setRemoveSubsFromEvent"
                    @showLog="showEventLog">
          </SubsUser>
        </template>
        <!-- SCHEDULER -->
        <template v-if="localSchedulerView">
          <div class="sub-scheduler">
            <div class="sub-scheduler__header">
              <div class="sub-scheduler__header__left">
                <div class="sub-scheduler__filters sub-scheduler__filters__subzen">
                  <SchedulerSort
                    :selected="selectedSort"
                    :locale="locale"
                    :groupTags="tags"
                    :selectedGroupTags="priorityGroupTags"
                    @onSortBy="onSortBy"
                    @toggleGroupTag="togglePriorityGroupTag"
                    @clearAllGroupTags="clearAllPriorityGroupTags">
                  </SchedulerSort>
                  <transition name="fadeInLeft">
                    <scheduler-prioritizer
                      v-if="selectedSort === 'subzen'"
                      :propsIsPrioritizerActive="isPrioritizerActive"
                      @setIsPrioritizerActive="isPrioritizerActive = $event">
                    </scheduler-prioritizer>
                  </transition>
                </div>
              </div>
              <div class="sub-scheduler__header__right sub-scheduler__header__right__filters">
                <Tabs
                  v-if="hasAbsentUsers"
                  :flatDesign="true"
                  :tabs="lessonViewTabs"
                  :selected="selectedLessonViewTabId"
                  @select="onLessonViewTabSelect"
                />
                <!-- Klassefilter -->
                <DoFiltersFilter
                  :title="i18n[locale]['in_the_class_this_day']"
                  :tags="classTags"
                  :selectedTags="selectedClassFilters"
                  @select="toggleClassFilter"
                  @clear="clearAllClassFilters"
                >
                </DoFiltersFilter>
                <!-- Filter på fraværende brugere (grupper/tags) -->
                <DoFiltersFilter
                  :title="i18n[locale]['absent']"
                  :tags="tags"
                  :selectedTags="appliedFiltersAbsentUsers"
                  @select="toggleAbsentFilter"
                  @clear="clearAllAbsentFilters"
                >
                </DoFiltersFilter>
                <DoFilters
                  :tags="tags"
                  :selectedTags="appliedFilters"
                  @select="toggleTag"
                  @clear="clearAllTags"
                  :selectedQuickFilters="selectedQuickFilters"
                  @toggleQuickFilter="toggleQuickFilter($event)"
                  @clearAll="clearAllFiltersForSubs"
                  :eventTitleTags="eventTitleTags"
                  :selectedEventTitles="selectedEventTitleFilters"
                  @selectEventTitle="toggleEventTitleFilter"
                  @clearEventTitles="clearAllEventTitleFilters"
                  :isSortable="true"
                  :sortItems="filterSortItems"
                  :isSortActive="isFilterSortActive"
                  @sortItems="setFilterSortOrder"
                  @toggleSort="setIsFilterSortActive"
                  :isPresenceOption="true"
                  :groupsRequiringPresence="groupsRequiringPresence"
                  @togglePresenceGroup="togglePresenceGroup">
                </DoFilters>
              </div>
            </div>
            <scheduler
              :title="text"
              :subsTitle="subsHeadline"
              :items="absentAndSubs"
              :isSortOnName="sortOnName"
              :date="getDate"
              :isSubStatusPendingActive="isSubStatusPendingActive"
              :allCalendarsInHash="allCalendarsInAHash"
              :longAbsentListInHash="longAbsentListInHash"
              :isHideEventsNotOverlappingAbsence="isHideEventsNotOverlappingAbsence"
              :selectedEventTitleFilters="selectedEventTitleFilters"
              :selectedQuickFilters="selectedQuickFilters"
              :selectedClassFilters="selectedClassFilters"
              :selectedGroupFilters="appliedFilters"
              @removeSubs="setRemoveSubsFromEvent"
              @showLog="showEventLog"
              @setCreateEventOnUser="setCreateEventOnUser"
              @splitLesson="setSplitLesson"
              @editTimes="setEditTimesLesson"
              @setTheOutOfSchoolLesson="setTheOutOfSchoolLesson($event)"
              @setIsCancelLesson="setIsCancelLesson($event)">
            </scheduler>
          </div>
        </template>
      </div>
      <splitLesson
        v-if="activeSplitLesson !== null"
        :lesson="activeSplitLesson"
        @submitted="onSubmittedSplitEvent"
        @closeSplitEvent="closeSplitLesson">
      </splitLesson>
      <SubsEventEditTimes
        v-if="theEditTimesLesson"
        :lesson="theEditTimesLesson"
        @updated="onEditTimesUpdated"
        @close="setEditTimesLesson(null)">
      </SubsEventEditTimes>
      <AppModal
        :submitTxt="i18n[locale]['button_text']"
        v-if="isModal"
        @close="isModal = false"
        @submit="removeSubs">
        <h2 class="pad-10" slot="header">
          {{ i18n[locale]['remove_subs'] }}
        </h2>
        <div slot="body">
          <h2>{{ i18n[locale]['substitutes_for'] }}</h2>
          <ul class="marg-20-top marg-20-bottom bold text-blue">
            <li v-for="sub in userAndLessonAndSubsToBeDeleted.subsForTheSubToBeRemoved" :key="sub">
              {{ allUsersInCalIdHash[sub].name }}
            </li>
          </ul>
          <p class="marg-5-bottom">
            {{ i18n[locale]['has_to_remove_subs'] }}
          </p>
          <p>
            {{ i18n[locale]['click_on_remove_all'] }}
          </p>
        </div>
      </AppModal>
      <AppModal
        submitTxt="OK"
        v-if="eventChangesEvent"
        :isShowFooter="false"
        @close="eventChangesEvent = null">
        <h2 class="pad-10" slot="header">{{ eventChangesEvent.title }}</h2>
        <div slot="body">
          <EventChanges :event-id="eventChangesEvent.id"></EventChanges>
        </div>
      </AppModal>
    </div>
    <SubsEditAbsence v-if="activeAbsenceReason"></SubsEditAbsence>
    <SubsUserPanel
      v-if="activeAbsentUser"
      :activeDate="getDate"
      :isSubStatusPendingActive="isSubStatusPendingActive"
      @close="setActiveAbsentUser(null)"
      @readEvents="readEvents"></SubsUserPanel>
    <CreateTask
      v-if="createEventOnUser && createEventOnUser.user"
      :user="createEventOnUser.user"
      :initDate="createEventOnUser.date"
      :showSubstituteOption="true"
      @close="setCreateEventOnUser (null)">
    </CreateTask>
    <ClassOnTrip
      v-if="isModalClassOnTrip"
      :initDate="getDate"
      @onCreatedEvents="readEvents"
      @close="setIsModalClassOnTrip(false)">
    </ClassOnTrip>
    <DModalOutOfSchoolLesson
      v-if="theOutOfSchoolLesson"
      :event="theOutOfSchoolLesson"
      :eventIdProp="'id'"
      @updated="onOutOfSchoolLessonUpdated"
      @close="setTheOutOfSchoolLesson(null)">
    </DModalOutOfSchoolLesson>
    <DModalCancelLesson
      v-if="theCancelLesson"
      :event="theCancelLesson"
      :eventIdProp="'id'"
      @updated="onCancelLessonUpdated"
      @close="setIsCancelLesson(null)">
    </DModalCancelLesson>
    <SendMessages v-if="schoolHasFeatureSMS"></SendMessages>
  </div>
</template>

<script>
import DatePicker from '../datepicker/DateSelector'
import SubstitutionCreate from './SubsCreate'
import SubsUser from './SubsUser'
import appFlipperButton from '../common/AppFlipperButtons'
import filterTags from '../Tagging/TaggingFilter'
import DoFilters from '../DoFilters/DoFilters.vue'
import scheduler from './Scheduler/Scheduler'
import appToggleButton from '../common/AppToggle'
import spinner from '../common/Loader'
import splitLesson from './SubsEventSplit'
import SubsEventEditTimes from './SubsEventEditTimes'
import SubsNote from './SubsNote'
import EventChanges from '../Log/EventChanges'
import AppModal from '../common/AppModal'
import {mapGetters} from 'vuex'
import docendo from '../../docendo'
import moment from 'moment'
import SchedulerPrioritizer from './Scheduler/SchedulerPrioritizer'
import SubsAbsentWithoutEnd from './SubsAbsentWithoutEnd'
import SubsEditAbsence from './SubsEditAbsence'
import SubsUserPanel from './SubsUserPanel/SubsUserPanel'
import eventbus from '../../eventbus'
import CreateTask from '../CreateTask/CreateTask.vue'
import ClassOnTrip from './ClassOnTrip/ClassOnTrip.vue'
import DFilters from '../common/DFilters.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import DModalOutOfSchoolLesson from '../Events/DModalOutOfSchoolLesson.vue'
import SchedulerSort from './Scheduler/SchedulerSort.vue'
import Tabs from '../common/Tabs.vue'
import DModalCancelLesson from '../Events/DModalCancelLesson.vue'
import SendMessages from '../SMS/SendMessages.vue'
import {mixin as clickaway} from 'vue-clickaway'

export default {
  mixins: [clickaway],
  components: {
    Tabs,
    SchedulerSort,
    DModalOutOfSchoolLesson,
    DModalCancelLesson,
    DoFiltersFilter,
    DFilters,
    ClassOnTrip,
    CreateTask,
    SubsUserPanel,
    SchedulerPrioritizer,
    scheduler,
    SubsUser,
    filterTags,
    DatePicker,
    SubstitutionCreate,
    appFlipperButton,
    appToggleButton,
    spinner,
    splitLesson,
    SubsEventEditTimes,
    AppModal,
    SubsNote,
    EventChanges,
    SubsAbsentWithoutEnd,
    SubsEditAbsence,
    DoFilters,
    SendMessages
  },
  data () {
    return {
      isHideEventsNotOverlappingAbsence: false,
      selectedSort: 'name',
      localSchedulerView: true,
      selectedQuickFilters: [],
      appliedFilters: [],
      appliedFiltersAbsentUsers: [],
      selectedClassFilters: [],
      selectedEventTitleFilters: [],
      selectedUser: null,
      sortOnName: true,
      isSortOnSubPools: false,
      sortOnSubPoolsAsc: true,
      sortOnDayPresence: false,
      activeSplitLesson: null,
      theEditTimesLesson: null,
      activeDSelect: null,
      isModal: false,
      userAndLessonAndSubsToBeDeleted: {
        user: {},
        lesson: {},
        subToBeRemoved: {},
        subsForTheSubToBeRemoved: []
      },
      eventChangesEvent: null,
      isFilterAbsentUsersWithHandledLessons: false,
      absentFilter: '',
      createEventOnUser: null,
      isModalClassOnTrip: false,
      theOutOfSchoolLesson: null,
      isPrioritizerActive: false,
      theCancelLesson: null,
      priorityGroupTags: [],
      filterSortOrder: [],
      isFilterSortActive: false,
      groupsRequiringPresence: [],
      isSubstitutionSettingsOpen: false
    }
  },
  computed: {
    schoolSettings () {
      return this.$store.state.schoolSettings || {}
    },
    includeReservationsInSubstitution () {
      return !!this.schoolSettings.includeReservationsInSubstitution
    },
    showOverlappingClassBadgeInSubstitution () {
      return this.schoolSettings.showOverlappingClassBadgeInSubstitution !== false
    },
    schoolFeatures () {
      return this.$store.getters.schoolFeatures
    },
    schoolHasFeatureSMS () {
      const features = ['substitute_sms', 'substitute_email']
      const hasFeatures = features.some(feature => this.schoolFeatures.includes(feature))
      const permissions = ['can_manage_school', 'can_send_substitute_email', 'can_send_substitute_sms']
      return hasFeatures && permissions.some(permission => this.userHasPermission(permission))
    },
    hasAbsentUsers () {
      return this.absentUsersFromUserSet.length > 0
    },
    lessonViewTabs () {
      return [
        { id: 'all', title: this.i18n[this.locale]['show_all_lessons'] },
        { id: 'hideNonOverlapping', title: this.i18n[this.locale]['hide_lessons_not_overlapping_absence'] }
      ]
    },
    selectedLessonViewTabId () {
      return this.isHideEventsNotOverlappingAbsence ? 'hideNonOverlapping' : 'all'
    },
    absentTabs () {
      return [
        { id: '', title: this.i18n[this.locale]['absent_today'] + ' (' + this.absentUsersFromUserSet.length + ')' },
        { id: 'handled', title: this.i18n[this.locale]['absent_handled'] + ' (' + this.absentUsersHandled.length + ')' },
        { id: 'unhandled', title: this.i18n[this.locale]['absent_unhandled'] + ' (' + this.absentUsersUnhandled.length + ')' }
      ]
    },
    currentAbsentTabId () {
      if (this.isAbsentUserFilter('handled')) return 'handled'
      if (this.isAbsentUserFilter('unhandled')) return 'unhandled'
      return ''
    },
    locale () {
      return this.$store.getters.locale
    },
    isClassOnTripSchool () {
      const country = this.$store.getters.schoolCountry
      return country && String(country).toUpperCase() === 'DK'
    },
    isLoadingSchoolCalenders () {
      return this.$store.state.isLoadingSchoolCalenders
    },
    allEvents () {
      return this.$store.state.subs.allEvents
    },
    allEventsInAHash () {
      return this.$store.state.subs.allEventsInAHash
    },
    allUsersAndEvents () {
      return this.$store.state.subs.allUsersAndEvents
    },
    allCalendarsInAHash () {
      return this.$store.getters.allCalendarsInHash
    },
    longAbsentListInHash () {
      return this.$store.getters.longAbsentListInHash
    },
    calsAndEventIds () {
      return this.$store.state.subs.allCalsWithEventIdsInHash
    },
    absentUsersHandledHash () {
      return this.$store.getters.absentUsersHandledHash
    },
    user () {
      return this.$store.state.user
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    ...mapGetters([
      'allUsersSortedOnName',
      'userSet',
      'absentUsersFromUserSet',
      'notAbsentUsersFromUserSet',
      'allUsersInCalIdHash',
      'absentUsersUnhandled',
      'absentUsersHandled',
      'isSubStatusPendingActive',
      'usersWithOngoingAbsenceInHash'
    ]),
    i18n () {
      return {
        da: {
          filters_saved_locally: 'Filtre gemt lokalt på din enhed',
          remove_subs: 'Fjern følgende vikarer',
          substitutes_for: 'Fjern følgende vikarer for ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' på ' + this.userAndLessonAndSubsToBeDeleted.lesson.title,
          has_to_remove_subs: 'Du er ved at fjerne en vikar, som har fået vikardækket lektionen af en anden vikar. Før du kan fortsætte, skal vikarer for ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' fjernes.',
          click_on_remove_all: 'Klik \'fjern alle\' for at gøre dette.',
          button_text: 'Fjern alle',
          absent_today: 'Fraværende i dag',
          absent_handled: 'Håndterede',
          absent_unhandled: 'Ikke-håndterede',
          absent: 'Fraværende',
          show_all_lessons: 'Vis alle lektioner',
          hide_lessons_not_overlapping_absence: 'Skjul lektioner uden fravær',
          in_the_class_this_day: 'I klassen denne dag'
        },
        en: {
          filters_saved_locally: 'Filters saved locally on your device',
          remove_subs: 'Remove substitute teachers',
          substitutes_for: 'Remove substitutes for ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' in ' + this.userAndLessonAndSubsToBeDeleted.lesson.title,
          has_to_remove_subs: 'You are about to delete a substitute teacher that has been substituted by another substitute teacher. To continue, the substitute teacher for ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' has to be removed',
          click_on_remove_all: 'Press \'remove all\' to continue.',
          button_text: 'Remove all',
          absent_today: 'Absent today',
          absent_handled: 'Handled',
          absent_unhandled: 'Unhandled',
          absent: 'Absent',
          show_all_lessons: 'Show all lessons',
          hide_lessons_not_overlapping_absence: 'Hide lessons without absence overlap',
          in_the_class_this_day: 'In the class this day'
        },
        nb: {
          filters_saved_locally: 'Filtre lagret lokalt på enheten din',
          remove_subs: 'Fjern vikar',
          substitutes_for: 'Fjern vikarer for ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' i ' + this.userAndLessonAndSubsToBeDeleted.lesson.title,
          has_to_remove_subs: 'Du kommer nå til å fjerne en vikar som har blitt erstattet av en annen vikar. For å fortsette skal vikarer for  ' + this.userAndLessonAndSubsToBeDeleted.subToBeRemoved.name + ' fjernes',
          click_on_remove_all: 'Klikk på \'fjern alle\' for å gjennomføre dette.',
          button_text: 'Fjern alle',
          absent_today: 'Fraværende i dag',
          absent_handled: 'Håndtert',
          absent_unhandled: 'Ikke håndtert',
          absent: 'Fraværende',
          show_all_lessons: 'Vis alle leksjoner',
          hide_lessons_not_overlapping_absence: 'Skjul leksjoner uten fravær',
          in_the_class_this_day: 'I klassen denne dag'
        }
      }
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    userCanSeeInternalNote () {
      const lang = this.locale
      if (!lang || lang.toLowerCase() === 'en') {
        return false
      }
      return this.userHasPermission('can_manage_school')
    },
    dataIsLoading () {
      return this.$store.state.subs.dataIsLoading
    },
    sortSubs () {
      return this.$store.state.subs.sortSubs
    },
    activeTab () {
      return this.localSchedulerView ? 1 : 0
    },
    allUsers () {
      return this.$store.state.subs.allUsers
    },
    getDate () {
      return this.$store.state.subs.date
    },
    isSubScoreActive () {
      return this.$store.state.subs.isSortSubActive
    },
    text () {
      return this.$t('vue2.sub_scheduler.absent_people') + ': ' + this.filteredAbsentUsers.length + ' ' + this.$t('vue2.sub_scheduler.out_of') + ' ' + this.absentUsersFromUserSet.length
    },
    subsHeadline () {
      return this.$t('vue2.sub_scheduler.potential_substitutes') + ': ' + this.filteredNotAbsentUsers.filter(user => !user.headline).length
    },
    isToday () {
      return moment.utc().format('YYYY-MM-DD') === moment(this.getDate).format('YYYY-MM-DD')
    },
    absentUsers () {
      if (this.absentFilter === 'unhandled') {
        return this.absentUsersUnhandled
      } else if (this.absentFilter === 'handled') {
        return this.absentUsersHandled
      } else {
        return this.absentUsersFromUserSet
      }
    },
    classTags () {
      const names = []
      const seen = {}
      const eventsHash = this.allEventsInAHash || {}
      const ids = Object.keys(eventsHash)
      for (let i = 0; i < ids.length; i++) {
        const ev = eventsHash[ids[i]]
        if (!ev || !ev.calendar_events || !Array.isArray(ev.calendar_events)) continue
        for (let j = 0; j < ev.calendar_events.length; j++) {
          const ce = ev.calendar_events[j]
          if (ce && ce.calendar_type === 'klass' && ce.name && !seen[ce.name]) {
            seen[ce.name] = true
            names.push(ce.name)
          }
        }
      }
      names.sort((a, b) => {
        if (a < b) return -1
        if (a > b) return 1
        return 0
      })
      return names
    },
    eventTitleTags () {
      const titles = []
      const seen = {}
      const skipTypes = {absence: true, available: true, presence: true, reservation: true}
      const eventsHash = this.allEventsInAHash || {}
      const ids = Object.keys(eventsHash)
      for (let i = 0; i < ids.length; i++) {
        const ev = eventsHash[ids[i]]
        if (!ev || !ev.title || skipTypes[ev.event_type] || seen[ev.title]) continue
        seen[ev.title] = true
        titles.push(ev.title)
      }
      titles.sort((a, b) => a.localeCompare(b, this.locale))
      return titles
    },
    filteredAbsentUsers () {
      let passedUsers = this.absentUsers
      const hasTagFilters = this.appliedFiltersAbsentUsers.length > 0
      const hasClassFilters = this.selectedClassFilters.length > 0

      // Hvis der ingen filtre er valgt, returner alle
      if (!hasTagFilters && !hasClassFilters) {
        return passedUsers
      }

      const selectedClasses = this.selectedClassFilters
      const eventsHash = this.allEventsInAHash || {}

      return passedUsers.filter(user => {
        if (user.headline) return true

        let tagMatch = false
        let classMatch = false

        if (hasTagFilters && user.user_tags && user.user_tags.length > 0) {
          tagMatch = user.user_tags.some(tag => this.appliedFiltersAbsentUsers.includes(tag))
        }

        if (hasClassFilters) {
          const dayAllEvents = user.dayAllEvents || []
          if (dayAllEvents.length > 0) {
            for (let i = 0; i < dayAllEvents.length; i++) {
              const ev = eventsHash[dayAllEvents[i]]
              if (!ev || !ev.calendar_events || !Array.isArray(ev.calendar_events)) continue
              for (let j = 0; j < ev.calendar_events.length; j++) {
                const ce = ev.calendar_events[j]
                if (ce && ce.calendar_type === 'klass' && ce.name && selectedClasses.indexOf(ce.name) > -1) {
                  classMatch = true
                  break
                }
              }
              if (classMatch) break
            }
          }
        }

        // Brugeren skal matches af enten tag‑filtre eller klasse‑filtre (eller begge)
        return (hasTagFilters && tagMatch) || (hasClassFilters && classMatch)
      })
    },
    filteredNotAbsentUsers () {
      let passedUsers = this.notAbsentUsersFromUserSet
      // Hvis der ikke er valgt nogle filtre overhovedet, returner alle
      const hasTagFilters = this.appliedFilters.length > 0
      const hasQuickFilters = this.selectedQuickFilters.length > 0
      const hasClassFilters = this.selectedClassFilters.length > 0
      const hasTitleFilters = this.selectedEventTitleFilters.length > 0
      if (!hasTagFilters && !hasQuickFilters && !hasClassFilters && !hasTitleFilters) {
        return passedUsers
      }

      const selectedClasses = this.selectedClassFilters
      const eventsHash = this.allEventsInAHash || {}

      const isAvailableActive = this.selectedQuickFilters.includes('sub_events_available')
      const isCanceledActive = this.selectedQuickFilters.includes('sub_events_canceled')
      const isTwoInClassActive = this.selectedQuickFilters.includes('sub_events_two_in_some_class')

      return passedUsers.filter(user => {
        if (user.headline) return true

        let quickOrTagMatch = false
        let classMatch = false

        // Quick‑filters + tags
        if (hasQuickFilters || hasTagFilters) {
          if (isAvailableActive && user.dayAvailability && user.dayAvailability.length > 0) {
            quickOrTagMatch = true
          }
          if (!quickOrTagMatch && isCanceledActive && user.canceledEvents && user.canceledEvents.length > 0) {
            quickOrTagMatch = true
          }
          if (!quickOrTagMatch && isTwoInClassActive && (
            (user.v_two_in_class_events && user.v_two_in_class_events.length > 0) ||
            (user.v_two_events_in_class_events && user.v_two_events_in_class_events.length > 0)
          )) {
            quickOrTagMatch = true
          }
          if (!quickOrTagMatch && hasTagFilters) {
            quickOrTagMatch = this.appliedFilters.some(tag => this.userMatchesGroup(user, tag))
          }
        }

        // Klasser
        if (hasClassFilters) {
          const dayAllEvents = user.dayAllEvents || []
          if (dayAllEvents.length > 0) {
            for (let i = 0; i < dayAllEvents.length; i++) {
              const ev = eventsHash[dayAllEvents[i]]
              if (!ev || !ev.calendar_events || !Array.isArray(ev.calendar_events)) continue
              for (let j = 0; j < ev.calendar_events.length; j++) {
                const ce = ev.calendar_events[j]
                if (ce && ce.calendar_type === 'klass' && ce.name && selectedClasses.indexOf(ce.name) > -1) {
                  classMatch = true
                  break
                }
              }
              if (classMatch) break
            }
          }
        }

        const matchesOtherFilters = (hasQuickFilters || hasTagFilters ? quickOrTagMatch : false) ||
          (hasClassFilters ? classMatch : false)
        const matchesTitle = hasTitleFilters && this.userMatchesEventTitleFilters(user, eventsHash)

        if (!hasQuickFilters && !hasTagFilters && !hasClassFilters) return matchesTitle
        if (!hasTitleFilters) return matchesOtherFilters
        return matchesOtherFilters || matchesTitle
      })
    },
    filterSortItems () {
      const activeItems = [
        ...this.selectedQuickFilters.map(value => ({key: 'quick:' + value, type: 'quick', value})),
        ...this.appliedFilters.map(value => ({key: 'group:' + value, type: 'group', value})),
        ...this.selectedEventTitleFilters.map(value => ({key: 'block:' + value, type: 'block', value}))
      ]
      const getOrderIdx = item => {
        const idx = this.filterSortOrder.indexOf(item.key)
        return idx > -1 ? idx : this.filterSortOrder.length
      }
      return activeItems
        .map((item, idx) => ({item, idx}))
        .sort((a, b) => (getOrderIdx(a.item) - getOrderIdx(b.item)) || (a.idx - b.idx))
        .map(({item}) => item)
    },
    absentAndSubs () {
      if (!this.localSchedulerView) return []

      const headline = {
        name: this.$t('vue2.sub_scheduler.potential_subs'),
        id: 12312312312312313123123123,
        dayAbsence: [],
        dayPresence: [],
        dayEvents: [],
        headline: true
      }

      let subs = [...this.filteredNotAbsentUsers]

      const sortFunctions = {
        subScore: (a, b) => b.subScore - a.subScore,
        subPools: (a, b) => this.sortOnSubPoolsAsc ? a.subPoolsDiff - b.subPoolsDiff : b.subPoolsDiff - a.subPoolsDiff,
        dayPresence: (a, b) => b.dayPresence.length - a.dayPresence.length,
        name: () => this.$helpers.sortArrOnName(subs)
      }

      const hasPriorityGroups = this.priorityGroupTags && this.priorityGroupTags.length > 0
      const hasFilterSort = this.isFilterSortActive && this.filterSortItems.length > 0
      const shouldSort = (this.isSubScoreActive && this.sortSubs !== 0) || (!this.isSubScoreActive && this.isSortOnSubPools && this.sortSubs !== 0) || this.sortOnDayPresence || this.sortOnName || hasPriorityGroups || hasFilterSort

      if (shouldSort) {
        subs = subs.filter(user => !user.headline)

        if (this.isSubScoreActive && this.sortSubs !== 0) {
          subs.sort(sortFunctions.subScore)
        } else if (this.isSortOnSubPools && this.sortSubs !== 0) {
          subs.sort(sortFunctions.subPools)
        } else if (this.sortOnDayPresence) {
          subs.sort(sortFunctions.dayPresence)
        } else if (this.sortOnName) {
          sortFunctions.name()
        }

        // Flyt brugere med prioritetsgrupper til toppen
        if (hasPriorityGroups) {
          const priorityUsers = []
          const otherUsers = []
          for (let i = 0; i < subs.length; i++) {
            const user = subs[i]
            const userTags = user.user_tags || []
            const hasPriorityTag = userTags.some(tag => this.priorityGroupTags.includes(tag))
            if (hasPriorityTag) {
              priorityUsers.push(user)
            } else {
              otherUsers.push(user)
            }
          }
          subs = [...priorityUsers, ...otherUsers]
        }

        // Sortering fra filter-dropdown har forrang; ovenstående bruges inden for hver gruppe
        if (hasFilterSort) {
          const eventsHash = this.allEventsInAHash || {}
          const items = this.filterSortItems
          const getRank = user => {
            const idx = items.findIndex(item => this.userMatchesFilterSortItem(user, item, eventsHash))
            return idx > -1 ? idx : items.length
          }
          subs = subs
            .map((user, idx) => ({user, idx, rank: getRank(user)}))
            .sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx))
            .map(({user}) => user)
        }

        subs.unshift(headline)
      } else if (subs.length === 0 || !subs[0].headline) {
        subs.unshift(headline)
      }

      return [...this.filteredAbsentUsers, ...subs]
    },
    activeAbsenceReason () {
      return this.$store.state.subs.activeAbsenceReason
    },
    activeAbsentUser () {
      return this.$store.state.subs.activeAbsentUser
    }
  },
  watch: {
    userSettings (val) {
      this.applyUserSettings(val)
    },
    isSubScoreActive (val) {
      this.sortOnName = !val
    },
    includeReservationsInSubstitution () {
      this.applyReservationSettingToLoadedEvents()
    }
  },
  methods: {
    onLessonViewTabSelect (tabId) {
      this.isHideEventsNotOverlappingAbsence = tabId === 'hideNonOverlapping'
      this.persistHideLessonsWithoutAbsence()
      if (tabId === 'hideNonOverlapping') {
        this.$store.dispatch('trackEvent', 'substitution_eventFilter_' + tabId)
      }
    },
    persistHideLessonsWithoutAbsence () {
      const key = 'd_hide_lessons_without_absence_' + this.schoolId
      if (this.isHideEventsNotOverlappingAbsence) {
        this.$helpers.setLocalStorage(key, '1')
      } else {
        this.$helpers.removeLocalStorage(key)
      }
    },
    onAbsentTabSelect (tabId) {
      this.setAbsentFilter(tabId)
    },
    setTheOutOfSchoolLesson (obj = null) {
      this.theOutOfSchoolLesson = obj
    },
    setIsCancelLesson (obj = null) {
      this.theCancelLesson = obj
    },
    onCancelLessonUpdated () {
      this.setIsCancelLesson(null)
      this.readEvents()
      this.$nextTick(_ => {
        const el = document.getElementById('djs-body-click')
        if (el) {
          el.click()
        }
      })
    },
    onOutOfSchoolLessonUpdated () {
      this.setTheOutOfSchoolLesson(null)
      this.readEvents()
      this.$nextTick(_ => {
        const el = document.getElementById('djs-body-click')
        if (el) {
          el.click()
        }
      })
    },
    setIsModalClassOnTrip (bool) {
      this.isModalClassOnTrip = bool
    },
    setCreateEventOnUser (userAndDateObj) {
      this.createEventOnUser = userAndDateObj
    },
    userHasPermission (val) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(val) > -1 || this.user.admin
      } else {
        return true
      }
    },
    setActiveAbsentUser (user) {
      this.$store.commit('setActiveAbsentUser', user)
    },
    setAbsentFilter (filter) {
      this.absentFilter = filter
    },
    isAbsentUserFilter (filter) {
      return this.absentFilter === filter
    },
    showEventLog (event) {
      this.eventChangesEvent = event
    },
    setActiveDSelect (userAndLessonId) {
      this.activeDSelect = userAndLessonId
    },
    setRemoveSubsFromEvent (userAndLessonAndSubsForUser) {
      this.userAndLessonAndSubsToBeDeleted = userAndLessonAndSubsForUser
      this.isModal = true
    },
    removeSubs () {
      // Remove subs for the sub
      const lesson = this.userAndLessonAndSubsToBeDeleted.lesson
      this.userAndLessonAndSubsToBeDeleted.subsForTheSubToBeRemoved.forEach(sub => {
        this.removeSub(this.allUsersInCalIdHash[sub], this.userAndLessonAndSubsToBeDeleted.subToBeRemoved, lesson)
      })
      // Remove the sub
      this.removeSub(this.userAndLessonAndSubsToBeDeleted.subToBeRemoved, this.userAndLessonAndSubsToBeDeleted.user, lesson)
      this.setSubStatusOnLesson(this.userAndLessonAndSubsToBeDeleted.user, this.userAndLessonAndSubsToBeDeleted.subToBeRemoved, lesson)
      this.isModal = false
      // this.userAndLessonAndSubsToBeDeleted = {}
    },
    removeSub (sub, subFor, lesson) {
      docendo.eventRemoveSubstitute(lesson.id, sub.id, subFor.id, data => {
        this.$store.commit('updateEventOnAllEventsInAHash', JSON.parse(data))
        this.$store.commit('removeEventOnAllUsersWithEvents', {
          cal_id: sub.user_calendar_id,
          event_id: lesson.id
        })
      })
    },
    setSubStatusOnLesson (user, sub, lesson) {
      let statusData = lesson.substitution_status ? JSON.parse(lesson.substitution_status) : {}
      statusData[user.user_calendar_id] = {'status': 0}
      statusData[sub.user_calendar_id] = {'status': 0}
      docendo.updateEvent(lesson.school_id, lesson.id, {substitution_status: JSON.stringify(statusData)}, success => {
        this.$store.commit('updateSubStatusOnEvent', {
          'eventId': lesson.id,
          'status': JSON.stringify(statusData)
        })
      })
    },
    onSortBy (selected, saveInLocalStorage = true) {
      this.resetSort()
      this.selectedSort = selected
      if (selected === 'subzen') {
        this.$store.commit('setIsSubScoreActive', true)
      }
      if (selected === 'pools') {
        this.isSortOnSubPools = true
      }
      if (selected === 'poolsAsc') {
        this.isSortOnSubPools = true
        this.sortOnSubPoolsAsc = true
      }
      if (selected === 'name') {
        this.sortOnName = true
      }
      if (selected === 'workHours') {
        this.sortOnDayPresence = true
      }
      this.$store.commit('setSortSubsAgain')
      const saveSortSetting = {'selectedSubSchedulerSorting': selected}
      if (saveInLocalStorage) {
        this.$store.dispatch('updateUserSettings', saveSortSetting)
      }
    },
    resetSort () {
      this.sortOnName = false
      this.isSortOnSubPools = false
      this.sortOnSubPoolsAsc = false
      this.sortOnDayPresence = false
      this.$store.commit('setIsSubScoreActive', false)
    },
    setIsSubScoreActive (boolean) {
      this.$store.dispatch('updateIsSubZenActive', boolean)
    },
    setSchedulerView (num) {
      if (num === 1) {
        this.localSchedulerView = true
        this.$helpers.removeLocalStorage('subListView')
      } else {
        this.localSchedulerView = false
        this.$helpers.setLocalStorage('subListView', true)
      }
      this.setActiveDSelect(null)
    },
    prevDay () {
      this.$store.commit('setSubDate', moment(this.getDate).subtract(1, 'days'))
      this.readEvents()
      this.$store.dispatch('subsGetLongAbsenceList')
      this.setActiveDSelect(null)
    },
    nextDay () {
      this.$store.commit('setSubDate', moment(this.getDate).add(1, 'days'))
      this.readEvents()
      this.$store.dispatch('subsGetLongAbsenceList')
      this.setActiveDSelect(null)
    },
    setToday () {
      this.$store.commit('setSubDate', moment.utc().format('YYYY-MM-DD'))
      this.readEvents()
      this.$store.dispatch('subsGetLongAbsenceList')
      this.setActiveDSelect(null)
    },
    setDate (date) {
      this.$store.commit('setSubDate', date)
      this.readEvents()
      this.$store.dispatch('subsGetLongAbsenceList')
      this.setActiveDSelect(null)
    },
    print () {
      window.print()
    },
    updateLocalStorage (keyStr, items) {
      const key = keyStr + '_' + this.schoolId
      if (items.length > 0) {
        this.$helpers.setLocalStorage(key, JSON.stringify(items))
      } else {
        this.$helpers.removeLocalStorage(key)
      }
    },
    toggleQuickFilter (quickFilter) {
      const val = quickFilter.value
      const idx = this.selectedQuickFilters.indexOf(val)
      if (idx > -1) {
        this.selectedQuickFilters.splice(idx, 1)
      } else {
        this.selectedQuickFilters.push(val)
      }
      this.updateLocalStorage('d_applied_event_filters_subs', this.selectedQuickFilters)
    },
    setTagFromFilter (tag) {
      this.appliedFilters.push(tag)
      this.updateLocalStorage('d_applied_filters_subs', this.appliedFilters)
      eventbus.toast = {text: this.i18n[this.locale]['filters_saved_locally'], 'success': true, position: 'bottomLeft'}
    },
    removeTagFromFilter (tag) {
      this.appliedFilters.splice(this.appliedFilters.indexOf(tag), 1)
      this.updateLocalStorage('d_applied_filters_subs', this.appliedFilters)
    },
    toggleTag (tag) {
      const idx = this.appliedFilters.indexOf(tag)
      if (idx > -1) {
        this.removeTagFromFilter(tag)
      } else {
        this.setTagFromFilter(tag)
      }
    },
    clearAllTags () {
      this.appliedFilters = []
      this.updateLocalStorage('d_applied_filters_subs', this.appliedFilters)
      this.selectedQuickFilters = []
      this.updateLocalStorage('d_applied_event_filters_subs', this.selectedQuickFilters)
    },
    clearAllFiltersForSubs () {
      this.clearAllTags()
      this.clearAllEventTitleFilters()
    },
    userMatchesEventTitleFilters (user, eventsHash) {
      const selectedTitles = this.selectedEventTitleFilters
      if (!selectedTitles.length) return true
      const skipTypes = {absence: true, available: true, presence: true, reservation: true}
      const dayAllEvents = user.dayAllEvents || []
      for (let i = 0; i < dayAllEvents.length; i++) {
        const ev = eventsHash[dayAllEvents[i]]
        if (ev && ev.title && !skipTypes[ev.event_type] && selectedTitles.indexOf(ev.title) > -1) {
          return true
        }
      }
      return false
    },
    userMatchesFilterSortItem (user, item, eventsHash) {
      if (item.type === 'group') {
        return this.userMatchesGroup(user, item.value)
      }
      if (item.type === 'block') {
        const skipTypes = {absence: true, available: true, presence: true, reservation: true}
        return (user.dayAllEvents || []).some(eventId => {
          const ev = eventsHash[eventId]
          return ev && ev.title === item.value && !skipTypes[ev.event_type]
        })
      }
      switch (item.value) {
        case 'sub_events_canceled':
          return !!user.canceledEvents && user.canceledEvents.length > 0
        case 'sub_events_available':
          return !!user.dayAvailability && user.dayAvailability.length > 0
        case 'sub_events_two_in_some_class':
          return (!!user.v_two_in_class_events && user.v_two_in_class_events.length > 0) ||
            (!!user.v_two_events_in_class_events && user.v_two_events_in_class_events.length > 0)
        default:
          return false
      }
    },
    userMatchesGroup (user, tag) {
      if (!user.user_tags || !user.user_tags.includes(tag)) return false
      if (!this.groupsRequiringPresence.includes(tag)) return true
      return !!user.dayPresence && user.dayPresence.length > 0
    },
    togglePresenceGroup (tag) {
      const idx = this.groupsRequiringPresence.indexOf(tag)
      if (idx > -1) {
        this.groupsRequiringPresence.splice(idx, 1)
      } else {
        this.groupsRequiringPresence.push(tag)
      }
      this.updateLocalStorage('d_groups_requiring_presence_subs', this.groupsRequiringPresence)
      this.$store.dispatch('trackEvent', idx > -1 ? 'substitution_group_presence_toggled_off' : 'substitution_group_presence_toggled_on')
    },
    setFilterSortOrder (keys) {
      this.filterSortOrder = keys
      this.updateLocalStorage('d_filter_sort_order_subs', this.filterSortOrder)
      this.$store.dispatch('trackEvent', 'substitution_filter_sort_changed')
    },
    setIsFilterSortActive (isActive) {
      this.isFilterSortActive = isActive
      this.$helpers.setLocalStorage('d_filter_sort_active_subs_' + this.schoolId, isActive ? '1' : '0')
      this.$store.dispatch('trackEvent', isActive ? 'substitution_filter_sort_toggled_on' : 'substitution_filter_sort_toggled_off')
    },
    toggleEventTitleFilter (title) {
      const idx = this.selectedEventTitleFilters.indexOf(title)
      if (idx > -1) {
        this.selectedEventTitleFilters.splice(idx, 1)
      } else {
        this.selectedEventTitleFilters.push(title)
        eventbus.toast = {text: this.i18n[this.locale]['filters_saved_locally'], 'success': true, position: 'bottomLeft'}
      }
      this.updateLocalStorage('d_applied_event_title_filters_subs', this.selectedEventTitleFilters)
      this.$store.dispatch('trackEvent', 'substitution_event_title_filter_toggled')
    },
    clearAllEventTitleFilters () {
      this.selectedEventTitleFilters = []
      this.updateLocalStorage('d_applied_event_title_filters_subs', this.selectedEventTitleFilters)
    },
    setTagFromFilterOnAbsentUsers (tag) {
      this.appliedFiltersAbsentUsers.push(tag)
      this.updateLocalStorage('d_applied_filters_absent', this.appliedFiltersAbsentUsers)
      eventbus.toast = {text: this.i18n[this.locale]['filters_saved_locally'], 'success': true, position: 'bottomLeft'}
    },
    removeTagFromFilterOnAbsentUsers (tag) {
      this.appliedFiltersAbsentUsers.splice(this.appliedFiltersAbsentUsers.indexOf(tag), 1)
      this.updateLocalStorage('d_applied_filters_absent', this.appliedFiltersAbsentUsers)
    },
    toggleAbsentFilter (tag) {
      const idx = this.appliedFiltersAbsentUsers.indexOf(tag)
      if (idx > -1) {
        this.removeTagFromFilterOnAbsentUsers(tag)
      } else {
        this.setTagFromFilterOnAbsentUsers(tag)
      }
    },
    clearAllAbsentFilters () {
      this.appliedFiltersAbsentUsers = []
      this.updateLocalStorage('d_applied_filters_absent', this.appliedFiltersAbsentUsers)
    },
    toggleClassFilter (tag) {
      const idx = this.selectedClassFilters.indexOf(tag)
      if (idx > -1) {
        this.selectedClassFilters.splice(idx, 1)
      } else {
        this.selectedClassFilters.push(tag)
      }
      this.$store.dispatch('trackEvent', 'substitution_class_filter_toggled')
      // Ingen localStorage for klasses lige nu – kan tilføjes senere
    },
    clearAllClassFilters () {
      this.selectedClassFilters = []
    },
    togglePriorityGroupTag (tag) {
      const idx = this.priorityGroupTags.indexOf(tag)
      if (idx > -1) {
        this.priorityGroupTags.splice(idx, 1)
      } else {
        this.priorityGroupTags.push(tag)
      }
      this.updateLocalStorage('d_priority_group_tags', this.priorityGroupTags)
      this.$store.commit('setSortSubsAgain')
    },
    clearAllPriorityGroupTags () {
      this.priorityGroupTags = []
      this.updateLocalStorage('d_priority_group_tags', this.priorityGroupTags)
      this.$store.commit('setSortSubsAgain')
    },
    getTags () {
      docendo.schoolTags(this.schoolId, data => {
        this.tags = data
      })
    },
    setSplitLesson (lesson) {
      this.activeSplitLesson = lesson
    },
    closeSplitLesson () {
      this.activeSplitLesson = null
    },
    onSubmittedSplitEvent () {
      this.activeSplitLesson = null
      this.closeSchedulerPopover()
    },
    setEditTimesLesson (lesson = null) {
      this.theEditTimesLesson = lesson
    },
    onEditTimesUpdated () {
      this.setEditTimesLesson(null)
      this.readEvents()
      this.$nextTick(_ => {
        const el = document.getElementById('djs-body-click')
        if (el) {
          el.click()
        }
      })
    },
    closeSchedulerPopover () {
      this.$store.commit('setActiveUser', null)
      this.$store.commit('setActiveEvent', null)
      this.$store.dispatch('setActiveSchedulerEvent', {
        'left': -10,
        'right': -10,
        'color': '',
        'oldSubScoreMeta': this.subScoreMeta,
        'obj': null
      })
    },
    closeSubstitutionSettings () {
      this.isSubstitutionSettingsOpen = false
    },
    toggleIncludeReservations () {
      const nextValue = !this.includeReservationsInSubstitution
      const nextSettings = {
        ...this.schoolSettings,
        includeReservationsInSubstitution: nextValue
      }
      this.$store.commit('setSchoolSettings', nextSettings)
      this.$store.dispatch('updateSchoolSettings', {
        includeReservationsInSubstitution: nextValue
      })
    },
    toggleShowOverlappingClassBadge () {
      const nextValue = !this.showOverlappingClassBadgeInSubstitution
      const nextSettings = {
        ...this.schoolSettings,
        showOverlappingClassBadgeInSubstitution: nextValue
      }
      this.$store.commit('setSchoolSettings', nextSettings)
      this.$store.dispatch('updateSchoolSettings', {
        showOverlappingClassBadgeInSubstitution: nextValue
      })
    },
    applyReservationSettingToLoadedEvents () {
      const rawEvents = this.$store.state.subs.allDayEventsRaw
      if (!rawEvents || !this.$store.state.subs.allUsersAndEvents) {
        return
      }
      this.$store.commit('setAllEventsOnUsers', rawEvents)
    },
    readEvents () {
      const checkIfUserIsMemberOfAllSchools = (schoolIdsToCall) => {
        const userSchools = this.user && this.user.schools ? this.user.schools : null
        if (!userSchools) {
          return true // If user is not loaded then try to call and see
        }
        const userSchoolIds = userSchools.map(school => school.id)
        // Check if every schoolId in schoolIdsToCall exists in userSchoolIds
        return schoolIdsToCall.every(id => userSchoolIds.includes(id))
      }
      const activeSchoolId = this.schoolId * 1
      // CUSTOM CALLS FOR SCHOOLS WITH MULTIPLE SCHOOL_IDS
      const isSchoolSorenKanneFaelles = activeSchoolId === 60494
      if (isSchoolSorenKanneFaelles) {
        const schoolIdsFromSorenKanne = [60489, 60490, 60491, 60492, 60494]
        const storeObj = {
          schoolIds: schoolIdsFromSorenKanne,
          isShowEventsFromOtherThanActiveSchool: true
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      const schoolIdsFromLillevang = [59488, 59489]
      const schoolIdsFromHerlevBySkole = [57481, 17269]
      const schoolIdsFromHornslet = [64478, 62460]
      const schoolIdsFromKildegaard = [23481, 57727]
      const schoolsIdsFromBorneby = [60458, 60459, 60460, 60495]
      const schoolIdsFromSolsort = [7272, 62084]
      const schoolIdsFrom64272 = [64272, 64273, 64274, 64275]
      const isIdxInHerlevArr = schoolIdsFromHerlevBySkole.indexOf(activeSchoolId)
      const isIdxInHornsletArr = schoolIdsFromHornslet.indexOf(activeSchoolId)
      const isIdxInKildeArr = schoolIdsFromKildegaard.indexOf(activeSchoolId)
      const isIdxInLillevangArr = schoolIdsFromLillevang.indexOf(activeSchoolId)
      const isIdxInBornebyArr = schoolsIdsFromBorneby.indexOf(activeSchoolId)
      const isIdsInSolsort = schoolIdsFromSolsort.indexOf(activeSchoolId)
      const isIdxIn64272Arr = schoolIdsFrom64272.indexOf(activeSchoolId)
      if (isIdxIn64272Arr > -1) {
        const storeObj = {
          schoolIds: schoolIdsFrom64272,
          isShowEventsFromOtherThanActiveSchool: false
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      if (isIdxInBornebyArr > -1) {
        const storeObj = {
          schoolIds: schoolsIdsFromBorneby,
          isShowEventsFromOtherThanActiveSchool: false
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      if (isIdsInSolsort > -1) {
        const storeObj = {
          schoolIds: schoolIdsFromSolsort,
          isShowEventsFromOtherThanActiveSchool: false
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      if (isIdxInLillevangArr > -1) {
        const storeObj = {
          schoolIds: schoolIdsFromLillevang,
          isShowEventsFromOtherThanActiveSchool: false
        }
        if (checkIfUserIsMemberOfAllSchools(schoolIdsFromLillevang)) {
          this.$store.dispatch('readAllEvents', storeObj)
        } else {
          this.$store.dispatch('getAllDayEvents')
        }
        return
      }
      if (isIdxInHerlevArr > -1) {
        const storeObj = {
          schoolIds: schoolIdsFromHerlevBySkole,
          isShowEventsFromOtherThanActiveSchool: false
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      if (isIdxInHornsletArr > -1) {
        const storeObj = {
          schoolIds: schoolIdsFromHornslet,
          isShowEventsFromOtherThanActiveSchool: true
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      if (isIdxInKildeArr > -1) {
        const storeObj = {
          schoolIds: schoolIdsFromKildegaard,
          isShowEventsFromOtherThanActiveSchool: false
        }
        this.$store.dispatch('readAllEvents', storeObj)
        return
      }
      this.$store.dispatch('getAllDayEvents')
    },
    applyUserSettings (val) {
      if (!val) return

      const {selectedSubSchedulerSorting, isSubZenActive, subZenOrder} = val

      if (selectedSubSchedulerSorting) {
        this.onSortBy(selectedSubSchedulerSorting, false)
      } else if (isSubZenActive) {
        this.onSortBy('subzen', true)
      }

      if (subZenOrder) {
        this.$store.commit('setSortingHierarchy', subZenOrder)
      }
    }
  },
  beforeMount () {
    this.$store.dispatch('getUserSubPools')
    this.$store.dispatch('subsGetLongAbsenceList')
    const localStorageAppliedFiltersForSubs = this.$helpers.getLocalStorage('d_applied_filters_subs_' + this.schoolId)
    const localStorageAppliedFiltersForAbsent = this.$helpers.getLocalStorage('d_applied_filters_absent_' + this.schoolId)
    this.appliedFilters = localStorageAppliedFiltersForSubs ? JSON.parse(localStorageAppliedFiltersForSubs) : []
    this.appliedFiltersAbsentUsers = localStorageAppliedFiltersForAbsent ? JSON.parse(localStorageAppliedFiltersForAbsent) : []
    const localStorageAppliedEventFiltersForSubs = this.$helpers.getLocalStorage('d_applied_event_filters_subs_' + this.schoolId)
    this.selectedQuickFilters = localStorageAppliedEventFiltersForSubs ? JSON.parse(localStorageAppliedEventFiltersForSubs) : []
    const localStorageAppliedEventTitleFilters = this.$helpers.getLocalStorage('d_applied_event_title_filters_subs_' + this.schoolId)
    this.selectedEventTitleFilters = localStorageAppliedEventTitleFilters ? JSON.parse(localStorageAppliedEventTitleFilters) : []
    const localStoragePriorityGroupTags = this.$helpers.getLocalStorage('d_priority_group_tags_' + this.schoolId)
    this.priorityGroupTags = localStoragePriorityGroupTags ? JSON.parse(localStoragePriorityGroupTags) : []
    const localStorageFilterSortOrder = this.$helpers.getLocalStorage('d_filter_sort_order_subs_' + this.schoolId)
    this.filterSortOrder = localStorageFilterSortOrder ? JSON.parse(localStorageFilterSortOrder) : []
    this.isFilterSortActive = this.$helpers.getLocalStorage('d_filter_sort_active_subs_' + this.schoolId) === '1'
    const localStorageGroupsRequiringPresence = this.$helpers.getLocalStorage('d_groups_requiring_presence_subs_' + this.schoolId)
    this.groupsRequiringPresence = localStorageGroupsRequiringPresence ? JSON.parse(localStorageGroupsRequiringPresence) : []
    const hideLessonsWithoutAbsence = this.$helpers.getLocalStorage('d_hide_lessons_without_absence_' + this.schoolId)
    this.isHideEventsNotOverlappingAbsence = hideLessonsWithoutAbsence === '1'
    if (this.$helpers.getLocalStorage('subListView')) {
      this.localSchedulerView = false
    }
    this.applyUserSettings(this.userSettings)
  },
  mounted () {
    this.$store.dispatch('getAllUsers')
    this.$store.dispatch('getUsers')
    this.readEvents()
    this.getTags()
    this.$store.dispatch('trackEvent', 'substitution_view_opened')
    window.addEventListener('vueTeamCalendarChanged', this.readEvents)
  },
  beforeDestroy () {
    window.removeEventListener('vueTeamCalendarChanged', this.readEvents)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.substitution {
  display: flex;
  flex-direction: column;
}

.substitution__header__left h1 {
  margin: 0 2px 0 0;
  line-height: 1.2;
}

.substitution__settings {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.substitution__settings__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background-color .2s, color .2s;
}

.substitution__settings__toggle:hover,
.substitution__settings--open .substitution__settings__toggle {
  background-color: rgba(0, 0, 0, .12);
  color: #333;
}

.substitution__settings__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  min-width: 280px;
  padding: 8px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, .18), 0 0 1px rgba(0, 0, 0, .12);
}

.substitution__settings__label {
  padding: 6px 10px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #8a8f98;
}

.substitution__settings__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  height: auto;
  min-height: 0;
  margin: 0;
  padding: 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  font-size: 13px;
  font-weight: 400 !important;
  line-height: 1.35;
  white-space: normal;
  color: #333;
  cursor: pointer;
  user-select: none;
  transition: background-color .15s;
}

.substitution__settings__item:hover {
  background-color: #f5f6f7;
}

.substitution__settings__item--active {
  background-color: #f0f6fc;
}

.substitution__settings__item--active:hover {
  background-color: #e8f1fa;
}

.substitution__settings__item-text {
  flex: 1;
  padding-top: 1px;
}

.substitution__settings__checkbox {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 0;
  border: 1.5px solid #c5c9ce;
  border-radius: 4px;
  background: #fff;
  position: relative;
  transition: background-color .15s, border-color .15s;
}

.substitution__settings__checkbox--checked {
  background: var(--primary, #468AD7);
  border-color: var(--primary, #468AD7);
}

.substitution__settings__checkbox--checked:after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.substitution__header__right {
  display: flex;
  justify-content: space-between;
}

.date-buttons {
  height: auto;
  white-space: nowrap;
}

.marg-5-right {
  margin-right: 5px;
}

.sub-scheduler {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
  border: 1px solid #d8d8d8;
}

.sub-header__subHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
}

.sub-scheduler__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: bold;
  border-bottom: 1px solid #d8d8d8;
}

.sub-scheduler__header__right,
.sub-scheduler__header__left {
  display: flex;
  align-items: center;
}

.sub-scheduler__header__right.sub-scheduler__header__right__filters {
  gap: 1rem;
  margin-right: 1rem;
}

.bttn--small {
  font-size: 10px;
  margin-right: 20px;
}

.sub-scheduler__filters {
  display: flex;
  align-items: center;
  border-left: 1px solid #ccc;
  padding: 10px;
  height: 40px;
}

.sub-scheduler__header__left .sub-scheduler__filters {
  border-left: 0;
}

.sub-scheduler__filters__subzen {
  position: relative;
}

.ie-warning {
  background: #ffcece;
  padding: 10px;
  color: #994545;
  border: 1px solid #994545;
  border-radius: 2px;
}

.absentUsersFilters {
  display: flex;
  background: #fff;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #d8d8d8;
}

.absentUsersFilters > div:not(:first-child) {
  margin-left: 0.5rem;
}

.absentUsersFilters__filter {
  cursor: pointer;
  background: transparent;
  border-radius: 8px;
  padding: 4px 8px;
  color: #9ca3af;
  font-weight: bold;
  transition-property: color;
  transition-duration: .15s;
  display: flex;
  align-items: center;
}

.absentUsersFilters__filter:hover {
  color: var(--dark-blue);
}

.absentUsersFilters__filter.absentUsersFilters__filter--active {
  background-color: #eee;
  color: var(--dark-blue);
}

.absentUsersFilters__filter__number {
  margin-right: 4px;
}

.d-modal__body--edit-absence {
  width: 600px;
}

.d-modal__body--edit-absence .d-modal__buttons {
  padding-top: 50px;
}

.sub-scheduler__filters label {
  margin-right: 0.5rem;
}

.sub-scheduler__filters.sub-scheduler__filters--active {
  background: aliceblue;
}

.sub-scheduler__filters.sub-scheduler__filters--active label.d-label {
  color: #222
}

.aku-button {
  display: flex;
  width: auto;
  margin-right: 1rem;
  align-items: center;
  height: 100%;
  min-height: 38px;
  white-space: nowrap;
}

</style>



// WEBPACK FOOTER //
// Subs.vue?b08b0184